import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';
import ImageUploader from './components/ImageUploader';
import ImagePreview from './components/ImagePreview';
import ResultDisplay from './components/ResultDisplay';
import ConsentBanner from './components/ConsentBanner';
import './App.css';
import usePageTracking from './hooks/usePageTracking';

function AppContent() {
  const location = useLocation();
  const [consentGiven, setConsentGiven] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [outputType, setOutputType] = useState('json');
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const storedConsent = localStorage.getItem('cookieConsent');
    if (storedConsent === 'true') {
      setConsentGiven(true);
    }
  }, []);

  useEffect(() => {
    if (consentGiven) {
      ReactGA.initialize('G-5ETZ47PGD0');
    }
  }, [consentGiven]);

  usePageTracking(location, consentGiven);

  const handleAcceptConsent = () => {
    setConsentGiven(true);
    localStorage.setItem('cookieConsent', 'true');
  };

  const handleDeclineConsent = () => {
    setConsentGiven(false);
    localStorage.setItem('cookieConsent', 'false');
  };

  const handleFileSelect = (file) => {
    setSelectedFile(file);
  };

  const handleOutputTypeChange = (event) => {
    setOutputType(event.target.value);
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      alert('Please select a file first.');
      return;
    }

    setIsLoading(true);
    document.body.style.cursor = 'wait';

    // Convert the selected file to base64
    const reader = new FileReader();
    reader.readAsDataURL(selectedFile);
    reader.onload = async () => {
      const base64Image = reader.result.split(',')[1];
      const imageUrl = `data:${selectedFile.type};base64,${base64Image}`;

      // Create the request body
      const requestBody = {
        url: imageUrl
      };

      try {
        const response = await fetch(`https://ai-receipts-437417.uc.r.appspot.com/recognize_receipts_${outputType}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': '96Ot7TsH9uM3xhwjch9sq3x4IOwv5pTt3cCOpPjiGg7TPRTaWLsSPvDOoJsPrzFk'
          },
          body: JSON.stringify(requestBody)
        });

        if (outputType === 'json') {
          const data = await response.json();
          setResult(JSON.stringify(data, null, 2));
        } else {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'receipt_data.xlsx';
          document.body.appendChild(a);
          a.click();
          a.remove();
        }
      } catch (error) {
        console.error('Error:', error);
        setResult('An error occurred while processing the request.');
      } finally {
        setIsLoading(false);
        document.body.style.cursor = 'default';
      }
    };
  };

  if (!consentGiven) {
    return (
      <div className="App">
        <h1>AI-Powered Receipt Digitalization</h1>
        <p>Convert your paper receipts into digital format quickly and accurately with our AI-powered tool.</p>
        <ConsentBanner 
          onAccept={handleAcceptConsent} 
          onDecline={handleDeclineConsent} 
        />
        <p>Please accept the consent to use the application.</p>
      </div>
    );
  }
  
  return (
    <div className="App">
      {!consentGiven && (
        <ConsentBanner 
          onAccept={handleAcceptConsent} 
          onDecline={handleDeclineConsent} 
        />
      )}
      <h1>AI-Powered Receipt Scanner</h1>
      <p>Convert your paper receipts into digital format quickly and accurately with our AI-powered tool.</p>
      <ImageUploader onFileSelect={handleFileSelect} />
      <ImagePreview file={selectedFile} />
      <div className="output-options">
        <label>
          <input
            type="radio"
            value="json"
            checked={outputType === 'json'}
            onChange={handleOutputTypeChange}
          />
          JSON
        </label>
        <label>
          <input
            type="radio"
            value="excel"
            checked={outputType === 'excel'}
            onChange={handleOutputTypeChange}
          />
          Excel
        </label>
      </div>
      <button onClick={handleSubmit} disabled={isLoading}>
        {isLoading ? 'Processing...' : 'Go'}
      </button>
      {result && <ResultDisplay result={result} />}
    </div>
  );
}

export default AppContent;
